import { useState } from "react";
import { connections } from "./api";
import { StatusBadge } from "../shell/ui";
import { Field } from "../components/rumonai";

export function SecretEditor({
  namespace,
  name,
  label,
  description,
  present,
  prefix = "",
  clearable = false,
  onStored,
  onFailed,
}: {
  namespace: string;
  name: string;
  label: string;
  description?: string;
  present: boolean | null;
  prefix?: string;
  /**
   * Whether a saved key may be cleared from here (ruling 2026-09-30): a
   * settings page offers it; a request for one key does not.
   */
  clearable?: boolean;
  onStored: () => void;
  onFailed?: () => void;
}) {
  const [value, setValue] = useState("");
  const [keyName, setKeyName] = useState(prefix || name);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(present !== true || Boolean(prefix));
  const [clearing, setClearing] = useState(false);
  const [result, setResult] = useState<"saved" | "failed" | "cleared" | "clear_failed" | null>(null);
  // Cleared here counts before the page has read it back.
  const filled = result === "cleared" ? false : result === "saved" || present === true;
  const clear = async () => {
    setBusy(true);
    setResult(null);
    try {
      // Names only, and no reading to be stale against: the key is gone or
      // it is not, whatever else changed on the page meanwhile.
      await connections.reset(namespace, [name], null);
      setClearing(false);
      setEditing(true);
      setResult("cleared");
      onStored();
    } catch {
      setResult("clear_failed");
      onFailed?.();
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      className="connection-secret"
      onSubmit={async (event) => {
        event.preventDefault();
        if (busy || !value) return;
        setBusy(true);
        setResult(null);
        try {
          const response = await connections.secret(
            namespace,
            prefix ? keyName : name,
            value,
          );
          if (!response.stored) throw new Error("not stored");
          setValue("");
          setResult("saved");
          if (!prefix) setEditing(false);
          onStored();
        } catch {
          // Do not echo a server error: it could contain the submitted credential.
          setValue("");
          setResult("failed");
          onFailed?.();
        } finally {
          setBusy(false);
        }
      }}
    >
      <div className="connection-secret-head">
        <strong>{label || name}</strong>
        <StatusBadge
          tone={filled ? "neutral" : "warning"}
          label={
            filled ? "已填写" : present === null && result !== "cleared" ? "状态未确认" : "未填写"
          }
        />
      </div>
      {description ? <p className="micro">{description}</p> : null}
      {!editing ? <div className="secret-stored-row">
        <span className="secret-mask" aria-label="已保存的密钥，不显示内容">••••••••••••</span>
        {clearing ? <>
          <button type="button" className="btn" data-clear-confirm disabled={busy} onClick={() => { void clear(); }}>
            {busy ? "正在清除…" : "确认清除"}
          </button>
          <button type="button" className="btn" disabled={busy} onClick={() => setClearing(false)}>取消</button>
        </> : <>
          <button type="button" className="btn" onClick={() => setEditing(true)}>更换密钥</button>
          {clearable ? <button type="button" className="btn btn-quiet" data-clear onClick={() => setClearing(true)}>
            清除密钥
          </button> : null}
        </>}
      </div> : <>
      {prefix ? (
        <Field label="具体密钥名称" hint={<>名称须以 {prefix} 开头。</>}>
          <input
            name="secret_name"
            value={keyName}
            autoComplete="off"
            onChange={(event) => setKeyName(event.target.value)}
            disabled={busy}
          />
        </Field>
      ) : null}
      <Field label={<span className="visually-hidden">{label || name} 密钥</span>}
        error={result === "failed" ? "保存结果未确认，请重新填写。" : undefined}>
        <input
          name={prefix ? keyName : name}
          type="password"
          autoComplete="new-password"
          spellCheck={false}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          disabled={busy}
          placeholder={
            present && result !== "cleared"
              ? "新密钥（留空不修改）"
              : "粘贴密钥"
          }
        />
      </Field>
      <button
        className="btn"
        type="submit"
        disabled={
          busy ||
          !value ||
          Boolean(prefix && (!keyName.startsWith(prefix) || keyName === prefix))
        }
      >
        {busy ? "正在保存…" : "保存密钥"}
      </button>
      {!prefix && filled ? <button type="button" className="btn" disabled={busy}
        onClick={() => { setValue(""); setEditing(false); setResult(previous => previous === "saved" ? previous : null); }}>
        取消更换
      </button> : null}
      </>}
      {result && result !== "failed" ? (
        <p role={result === "clear_failed" ? "alert" : "status"} className="micro">
          {result === "saved"
            ? "密钥已保存，连接尚未验证。"
            : result === "cleared"
              ? "密钥已清除。"
              : "没有清除成功，请重试。"}
        </p>
      ) : null}
    </form>
  );
}
